import classNames from 'classnames';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { ItemSelector, Text } from 'vet-bones/components';
import { alphaSort } from 'vet-bones/utils';
import { SelectItem, createSelectItem } from 'vet-bones/utils/select';

import {
  TEST_IDS,
  getProjectMappingRelationshipMappingCellTestId,
  getProjectMappingSelectItemTestId,
} from 'src/constants/testIds';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import { useRemoveRelationshipMapping } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveRelationshipMapping';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { Mapping } from 'src/containers/ProjectDiagram/types/data';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { RelationshipWithHierarchyData } from 'src/containers/ProjectDiagram/utils/getAllInheritedRelationships';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { addRelationshipMapping } from 'src/containers/ProjectDiagram/utils/mapping/addRelationshipMapping';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { useMappingFromRoute } from 'src/containers/ProjectMapping/hooks/useMappingFromRoute';
import { ProjectClassMappingRelationshipCellPrimaryIdentifier } from 'src/containers/ProjectMapping/ProjectClassMappingRelationshipSection/ProjectClassMappingRelationshipCellPrimaryIdentifier';
import { RelationshipMappingCell } from 'src/containers/ProjectMapping/types';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectMappingsSelector } from 'src/selectors/projectMappings';
import { projectRelationshipHierarchicalDomainAndRangesSelector } from 'src/selectors/projectRelationshipDomainAndRanges';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type ProjectClassMappingRelationshipMappingCellProps = {
  relationship: RelationshipWithHierarchyData;
  relationshipCellData: RelationshipMappingCell;
  setCell: (partialCell: Partial<RelationshipMappingCell>) => void;
};

export const ProjectClassMappingRelationshipCell: React.VFC<
  ProjectClassMappingRelationshipMappingCellProps
> = ({ relationship, relationshipCellData, setCell }) => {
  const {
    key: mappingKey,
    from: resourceKey,
    to: classKey,
  } = useMappingFromRoute() as Mapping & { to: string };
  const { projectId } = useRouterParams();
  const diagram = useDiagram();
  const { iri: relationshipIri } = relationship;
  const {
    classIri,
    classMappingIdentifier,
    isSource,
    mappingId,
    relationshipMappingKey,
  } = relationshipCellData;

  const removeRelationshipMapping = useRemoveRelationshipMapping();

  const labelsByIri = useReduxSelector(projectLabelsByIriSelector(projectId));
  const classMappings = useReduxSelector(projectMappingsSelector(projectId));
  const { domains, ranges } = useReduxSelector(
    projectRelationshipHierarchicalDomainAndRangesSelector(
      projectId,
      relationshipIri
    )
  );

  const classItems = React.useMemo(() => {
    const relevantSet = isSource ? domains : ranges;
    return Array.from(relevantSet)
      .map((domainOrRangeIri) =>
        createSelectItem(
          getLabelForIri(labelsByIri, domainOrRangeIri),
          domainOrRangeIri
        )
      )
      .sort(alphaSort((item) => item.text));
  }, [domains, isSource, labelsByIri, ranges]);
  const selectedClassItem =
    classItems.find((item) => item.value === classIri) || null;

  const classMappingByIri = React.useMemo(
    () =>
      classMappings.reduce<Record<string, Mapping>>((acc, mapping) => {
        if (!mapping.to || mapping.from !== resourceKey) {
          return acc;
        }

        const classIri = getModelConceptClassValue(mapping.to);
        acc[classIri] = mapping;

        return acc;
      }, {}),
    [classMappings, resourceKey]
  );

  const handleSelectClass = React.useCallback(
    (item: SelectItem | null) => {
      const newClassIri = item?.value;
      if (newClassIri === classIri) {
        return;
      }

      if (relationshipMappingKey) {
        removeRelationshipMapping(relationshipMappingKey);
      }

      const newIdentifier =
        newClassIri && newClassIri !== getModelConceptClassValue(classKey)
          ? classMappingByIri[newClassIri]?.primaryIdentifier
          : null;

      if (diagram && newIdentifier?.parts.length && newClassIri) {
        commitDiagramModel(diagram, (model) => {
          const newRelMapping = isSource
            ? addRelationshipMapping({
                fromClassIri: newClassIri,
                fromPrimaryIdentifier: newIdentifier,
                relationshipIri,
                to: mappingKey,
                value: mappingId,
              })(model)
            : addRelationshipMapping({
                from: mappingKey,
                relationshipIri,
                toClassIri: newClassIri,
                toPrimaryIdentifier: newIdentifier,
                value: mappingId,
              })(model);

          setCell({
            classIri: newClassIri,
            classMappingIdentifier: newIdentifier,
            mappingId: newRelMapping.value,
            relationshipMappingKey: newRelMapping.key,
          });
        });
      } else {
        setCell({
          classIri: item?.value || null,
          classMappingIdentifier: { baseIri: null, parts: [] },
          mappingId: uuid(),
          relationshipMappingKey: null,
        });
      }
    },
    [
      classIri,
      classKey,
      classMappingByIri,
      diagram,
      isSource,
      mappingId,
      mappingKey,
      relationshipIri,
      relationshipMappingKey,
      removeRelationshipMapping,
      setCell,
    ]
  );

  return (
    <div
      className="sd-project-mapping-relationship-mapping-cell"
      data-testid={getProjectMappingRelationshipMappingCellTestId(
        relationshipIri,
        isSource
          ? RelationshipDirection.INCOMING
          : RelationshipDirection.OUTGOING,
        !relationshipMappingKey ? '' : relationshipMappingKey
      )}
    >
      <div
        className={classNames(
          'sd-project-mapping-relationship-mapping-cell-class-selector',
          {
            'sd-project-mapping-relationship-mapping-cell-class-selector--padded':
              classIri,
          }
        )}
      >
        <Text>
          {copy.components.projectMapping.relationships.classSelector(isSource)}
        </Text>
        <ItemSelector.Single
          buttonText={selectedClassItem?.text || ''}
          clearable
          filterText={copy.components.defaults.filter}
          items={classItems}
          itemTestId={getProjectMappingSelectItemTestId}
          onSelect={handleSelectClass}
          selected={selectedClassItem}
          testId={TEST_IDS.projectMapping.relationships.mappings.classSelector}
        />
      </div>

      {selectedClassItem ? (
        <div className="sd-project-mapping-relationship-mapping-cell-identifier">
          <Text>
            {copy.components.projectMapping.relationships.identifierType(
              isSource
            )}
          </Text>
          <div>
            <ProjectClassMappingRelationshipCellPrimaryIdentifier
              classIri={classIri}
              currentIdentifier={classMappingIdentifier}
              isSource={isSource}
              mappingId={mappingId}
              relationshipIri={relationshipIri}
              relationshipMappingKey={relationshipMappingKey}
              setCell={setCell}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};
